import {
  ChangeDetectionStrategy,
  Component,
  Input,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { ProductsFilterStateService, ProductFilterSlot } from '../../state/products-filter-state.service';

/** Ceiling on a typed premium. Same figure as MAX_CONTRIBUTION on the /rates
 *  numeric picker, so a premium the rates page accepts is one this page accepts
 *  too — high enough for any real case, low enough that a stuck key can't
 *  produce a number the formatter renders as junk. */
export const MAX_PREMIUM = 10_000_000;  // $10,000,000

/**
 * Premium amount box for the products rail's "Surrenders & Premium" group.
 *
 * Replaced a pair of Min/Max Premium sliders (2026-09-24). Those looked like a
 * range but weren't: each held the same quantity — the premium the user intends
 * to place — and merely tested it against a different bound of the product's
 * band, so getting a sensible answer meant setting both to the same number and
 * nothing stopped you setting them to contradictory ones. One figure states it
 * directly:
 *
 *   Premium $25,000 → keep products whose band admits $25,000
 *                     (minPremium ≤ 25,000 ≤ maxPremium)
 *
 * Applies as you type, like every other picker in this rail. Digits only —
 * separators are re-inserted after each keystroke, the same way the /rates
 * Premium picker behaves, so the two pages feel identical.
 *
 * Chrome matches ProductChecklistPickerComponent's rail-field head/body so the
 * group still reads consistently with the checklist dims above it.
 */
@Component({
  selector: 'app-product-premium-input',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="rail-field" [class.is-open]="open()">
      <button type="button" class="rail-field-head" [attr.aria-expanded]="open()" (click)="toggleOpen()">
        <span class="rf-name">{{ label }}</span>
        @if (value() === null) {
          <span class="rf-allnone">All</span>
        } @else {
          <span class="rf-summary">{{ summaryText() }}</span>
        }
      </button>
      @if (open()) {
        <div class="rail-field-body">
          <div class="row">
            <span class="prefix" aria-hidden="true">$</span>
            <input
              class="num"
              type="text"
              inputmode="numeric"
              autocomplete="off"
              placeholder="0"
              maxlength="13"
              [value]="displayValue()"
              [attr.aria-label]="label"
              (input)="onInput($any($event.target))" />
          </div>
          <div class="prem-readout">
            @if (value() === null) {
              <span class="prem-none">No premium restriction</span>
            } @else {
              <span class="prem-value">{{ helperText() }}</span>
              <button type="button" class="prem-clear" (click)="clear()">Clear</button>
            }
          </div>
        </div>
      }
    </div>
  `,
  styles: [`
    :host { display: block; }
    .rail-field-head {
      width: 100%;
      display: flex; align-items: center; gap: 8px;
      padding: 7px 6px;
      background: transparent; border: none; cursor: pointer;
      font: inherit; text-align: left; border-radius: 6px;
      &:hover { background: var(--bg); }
    }
    .rf-name { font-size: 12.5px; font-weight: 600; color: var(--navy); }
    .rail-field:not(.is-open) .rf-name { font-weight: 500; color: var(--text2); }
    .rf-summary {
      margin-left: auto;
      font-size: 11.5px; font-weight: 600; color: var(--blue);
      white-space: nowrap; letter-spacing: 0;
    }
    .rf-allnone { margin-left: auto; font-size: 11px; color: var(--muted); font-style: italic; }
    .rail-field-body { padding: 4px 8px 10px 8px; }

    /* Input box copied from the /rates Premium picker
       (rates/rail/pickers/numeric-picker.component.scss) so the same filter
       looks the same on both pages. Class names kept identical to that file so
       the two are diffable. Both are component-scoped styles, so this is a copy
       rather than a shared sheet — keep them in step by hand.

       ONE deliberate difference: no outer margin. The rates picker sits in an
       unpadded popover and supplies its own 14px; here .rail-field-body already
       pads the field, and carrying the margin over would double it. */
    .row {
      display: flex;
      align-items: stretch;
      height: 36px;
      border: 1px solid var(--border);
      border-radius: 4px;
      background: var(--panel);
      overflow: hidden;

      &:focus-within {
        border-color: var(--blue);
        box-shadow: 0 0 0 3px rgba(42, 106, 168, 0.18);
      }
    }
    .prefix {
      width: 28px;
      display: grid;
      place-items: center;
      background: var(--bg);
      color: var(--muted);
      font-size: 14px;
      font-weight: 600;
      border-right: 1px solid var(--border-light);
    }
    .num {
      flex: 1;
      min-width: 0;
      border: none;
      outline: none;
      padding: 0 10px;
      font-family: 'Roboto Mono', ui-monospace, monospace;
      font-size: 14px;
      font-weight: 600;
      color: var(--text2);
      background: var(--panel);
    }
    .prem-readout {
      display: flex; align-items: center; gap: 8px;
      margin-top: 6px; min-height: 18px;
    }
    .prem-value { font-size: 11px; color: var(--text2); }
    .prem-none { font-size: 11px; color: var(--muted); font-style: italic; }
    .prem-clear {
      margin-left: auto;
      background: transparent; border: none; cursor: pointer;
      font: inherit; font-size: 10.5px; font-weight: 600;
      color: var(--blue); padding: 0;
      &:hover { text-decoration: underline; }
    }
  `],
})
export class ProductPremiumInputComponent {
  @Input({ required: true }) field!: keyof ProductFilterSlot;
  @Input({ required: true }) label!: string;

  /** Chip key this dimension clears under. */
  private static readonly CLEAR_KEY = 'premium';

  private readonly filterState = inject(ProductsFilterStateService);

  private readonly _userToggled = signal(false);
  private readonly _expanded = signal(false);

  constructor() {
    // Collapse when this dimension's Active-Filters chip is cleared, matching
    // ProductChecklistPickerComponent.
    effect(() => {
      const req = this.filterState.dimClearedRequest();
      untracked(() => {
        if (req.tick === 0) return;
        if (req.key === ProductPremiumInputComponent.CLEAR_KEY) {
          this._userToggled.set(true);
          this._expanded.set(false);
        }
      });
    });
  }

  protected readonly value = computed<number | null>(() => {
    this.filterState.version();
    return this.filterState.slot[this.field] as number | null;
  });

  /** Grouped for reading; the box holds digits + separators, never a `$`,
   *  which is rendered as a prefix outside the input. */
  protected readonly displayValue = computed<string>(() => {
    const v = this.value();
    return v === null ? '' : v.toLocaleString('en-US');
  });

  protected readonly summaryText = computed<string>(() => {
    const v = this.value();
    return v === null ? '' : '$' + v.toLocaleString('en-US');
  });

  protected open(): boolean {
    if (this._userToggled()) return this._expanded();
    return this.value() !== null;
  }

  toggleOpen(): void {
    const wasOpen = this.open();
    this._userToggled.set(true);
    this._expanded.set(!wasOpen);
  }

  /** Spells out the predicate so the direction isn't left to inference. */
  protected helperText(): string {
    const v = this.value();
    if (v === null) return '';
    return `Products accepting $${v.toLocaleString('en-US')}`;
  }

  onInput(input: HTMLInputElement): void {
    const raw = input.value.replace(/[^0-9]/g, '');
    const num = raw === '' ? null : Math.min(Number(raw), MAX_PREMIUM);
    // Write back immediately: the signal only re-renders the box when the
    // VALUE changes, so typing a separator-only edit (or hitting the cap)
    // would otherwise leave the caret next to unformatted text.
    input.value = num === null ? '' : num.toLocaleString('en-US');
    this.filterState.setSlotField(this.field, num as ProductFilterSlot[keyof ProductFilterSlot]);
    this.filterState.requestApplyFilters();
  }

  clear(): void {
    this.filterState.setSlotField(this.field, null as ProductFilterSlot[keyof ProductFilterSlot]);
    this.filterState.requestApplyFilters();
  }
}
